import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { vacancyService, studentService, applicationService } from '../services/api';
import { isStudent, isAuthenticated, getUserData } from '../utils/auth';
import './VacancyDetails.css';

function VacancyDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const driveId = searchParams.get('driveId');
  const companyId = searchParams.get('companyId');

  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    college: '',
    university: '',
    address: '',
    location: '',
    studentCode: '',
    universityRegNo: '',
    course: '',
    year: '',
    cgpa: '',
    question1: '',
    question2: '',
    question3: '',
    description: ''
  });

  const [resume, setResume] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [studentApproved, setStudentApproved] = useState(null);

  useEffect(() => {
    loadVacancyDetails();
  }, [id]);

  useEffect(() => {
    if (isAuthenticated() && isStudent()) {
      const userData = getUserData();
      const studentId = userData?.studentId || userData?.StudentId;
      if (studentId) {
        studentService.getStudentById(studentId).then((student) => {
          if (student) {
            setStudentApproved(!!student.IsApproved);
            setFormData((prev) => ({
              ...prev,
              fullName: student.Name || '',
              email: student.Email || '',
              phone: student.MobileNo || '',
              college: student.College?.Name || '',
              university: student.University?.Name || '',
              address: student.Address || '',
              location: student.Location || '',
              studentCode: student.Code || '',
              universityRegNo: student.UniversityRegNo || ''
            }));
          }
        }).catch(() => setStudentApproved(false));
      } else setStudentApproved(false);
    }
  }, []);

  const loadVacancyDetails = async () => {
    try {
      setLoading(true);
      const vacancies = await vacancyService.getVacancies();
      const foundVacancy = vacancies.find(v => v.Id === parseInt(id));
      if (foundVacancy) {
        setVacancy(foundVacancy);
      }
    } catch (error) {
      console.error('Error loading vacancy details:', error);
      alert('Error loading vacancy details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getBackLink = () => {
    if (driveId) {
      return `/drive/${driveId}`;
    } else if (companyId) {
      return `/company/${companyId}/jobs`;
    }
    return '/placement-drives';
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file only.');
        e.target.value = '';
        return;
      }
      setResume(file);
      setResumeFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!resume) {
      alert('Please upload your resume (PDF only)');
      return;
    }

    if (vacancy.Question1 && !formData.question1) {
      alert('Please answer question 1');
      return;
    }
    if (vacancy.Question2 && !formData.question2) {
      alert('Please answer question 2');
      return;
    }
    if (vacancy.Question3 && !formData.question3) {
      alert('Please answer question 3');
      return;
    }

    const userData = getUserData();
    const studentId = userData?.studentId || userData?.StudentId;
    if (!studentId) {
      alert('Please log in as a student to apply.');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('VacancyId', vacancy.Id);
      fd.append('StudentId', studentId);
      fd.append('Answer1', formData.question1 || '');
      fd.append('Answer2', formData.question2 || '');
      fd.append('Answer3', formData.question3 || '');
      fd.append('Description', formData.description || '');
      fd.append('Resume', resume);

      await applicationService.saveApplicationWithResume(fd);
      alert('Application submitted successfully! We will review your application and get back to you soon.');
      
      setFormData({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        college: formData.college,
        course: '',
        year: '',
        cgpa: '',
        question1: '',
        question2: '',
        question3: '',
        description: ''
      });
      setResume(null);
      setResumeFileName('');
    } catch (err) {
      alert('Failed to submit application: ' + (err.message || 'Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="vacancy-details-page">
        <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Loading vacancy details...</p>
        </div>
      </div>
    );
  }

  if (!vacancy) {
    return (
      <div className="vacancy-details-page">
        <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Vacancy not found.</p>
          <Link to={getBackLink()} className="back-link">← Go Back</Link>
        </div>
      </div>
    );
  }

  const studentLoggedIn = isAuthenticated() && isStudent();

  return (
    <div className="vacancy-details-page">
      <div className="container">
        <Link to={getBackLink()} className="back-link">← Go Back</Link>

        <div className="vacancy-info-card">
          <div className="vacancy-info-header">
            <div>
              <h1>{vacancy.Title}</h1>
              <p className="vacancy-company">{vacancy.Company?.Name || 'Company not specified'}</p>
            </div>
            {vacancy.LastDate && (
              <div className="vacancy-salary-badge">Due: {formatDate(vacancy.LastDate)}</div>
            )}
          </div>

          <div className="vacancy-info-details">
            {vacancy.PlacementDrive?.Title && (
              <div className="info-row">
                <span><strong>Placement Drive:</strong> {vacancy.PlacementDrive.Title}</span>
              </div>
            )}
            {vacancy.Date && (
              <div className="info-row">
                <span><strong>Posted Date:</strong> {formatDate(vacancy.Date)}</span>
                {vacancy.LastDate && (
                  <span><strong>Last Date to Apply:</strong> {formatDate(vacancy.LastDate)}</span>
                )}
              </div>
            )}
            {vacancy.Description && (
              <div className="description-box">
                <strong>Job Description:</strong>
                <p>{vacancy.Description}</p>
              </div>
            )}
            {vacancy.Conditions && (
              <div className="requirements-box">
                <strong>Conditions:</strong>
                <p>{vacancy.Conditions}</p>
              </div>
            )}
            {(vacancy.Question1 || vacancy.Question2 || vacancy.Question3) && (
              <div className="questions-box">
                <strong>Application Questions:</strong>
                {vacancy.Question1 && <p><strong>1.</strong> {vacancy.Question1}</p>}
                {vacancy.Question2 && <p><strong>2.</strong> {vacancy.Question2}</p>}
                {vacancy.Question3 && <p><strong>3.</strong> {vacancy.Question3}</p>}
              </div>
            )}
          </div>
        </div>

        {studentLoggedIn && studentApproved === false ? (
          <div className="application-form-card" style={{ padding: '1.5rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px' }}>
            <h2>Application not available</h2>
            <p>Your profile is pending approval by your college coordinator. You can apply for jobs only after your registration is approved. Please wait for the coordinator to review your details.</p>
          </div>
        ) : studentLoggedIn && studentApproved === null ? (
          <div className="application-form-card" style={{ padding: '1.5rem', textAlign: 'center' }}>
            <p>Checking your approval status...</p>
          </div>
        ) : studentLoggedIn && studentApproved ? (
          <div className="application-form-card">
            <h2>Application Form</h2>
            <p className="form-subtitle">Fill in your details and answer the questions to apply for this position</p>

          <form onSubmit={handleSubmit} className="application-form">
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="college">College *</label>
                  <input
                    type="text"
                    id="college"
                    name="college"
                    value={formData.college}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="university">University</label>
                  <input
                    type="text"
                    id="university"
                    name="university"
                    value={formData.university}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="studentCode">Student / Roll Code</label>
                  <input
                    type="text"
                    id="studentCode"
                    name="studentCode"
                    value={formData.studentCode}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="universityRegNo">University Reg No</label>
                  <input
                    type="text"
                    id="universityRegNo"
                    name="universityRegNo"
                    value={formData.universityRegNo}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="location">Location / City</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: '1 1 100%' }}>
                  <label htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Full address"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="course">Course *</label>
                  <select
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Course</option>
                    <option value="B.Tech">B.Tech</option>
                    <option value="M.Tech">M.Tech</option>
                    <option value="B.Sc">B.Sc</option>
                    <option value="M.Sc">M.Sc</option>
                    <option value="MBA">MBA</option>
                    <option value="BCA">BCA</option>
                    <option value="MCA">MCA</option>
                  </select>
                </div>
                <div className="form-row-inline">
                  <div className="form-group">
                    <label htmlFor="year">Year *</label>
                    <select
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      <option value="1">1st Year</option>
                      <option value="2">2nd Year</option>
                      <option value="3">3rd Year</option>
                      <option value="4">4th Year</option>
                      <option value="Final">Final Year</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cgpa">CGPA *</label>
                    <input
                      type="text"
                      id="cgpa"
                      name="cgpa"
                      value={formData.cgpa}
                      onChange={handleChange}
                      placeholder="e.g., 8.5"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

                <div className="form-section">
              <h3>Resume Upload</h3>
              <div className="form-group">
                <label htmlFor="resume">Upload Resume (PDF only) *</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,application/pdf"
                    onChange={handleResumeChange}
                    required
                  />
                  {resumeFileName && (
                    <span className="file-name">Selected: {resumeFileName}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Application Questions</h3>
              <p className="questions-note">Please answer all questions below (minimum 50 words each)</p>
              
              {vacancy.Question1 && (
                <div className="form-group">
                  <label htmlFor="question1">Question 1: {vacancy.Question1} *</label>
                  <textarea
                    id="question1"
                    name="question1"
                    rows="4"
                    value={formData.question1}
                    onChange={handleChange}
                    placeholder="Type your answer here..."
                    required={!!vacancy.Question1}
                  ></textarea>
                </div>
              )}

              {vacancy.Question2 && (
                <div className="form-group">
                  <label htmlFor="question2">Question 2: {vacancy.Question2} *</label>
                  <textarea
                    id="question2"
                    name="question2"
                    rows="4"
                    value={formData.question2}
                    onChange={handleChange}
                    placeholder="Type your answer here..."
                    required={!!vacancy.Question2}
                  ></textarea>
                </div>
              )}

              {vacancy.Question3 && (
                <div className="form-group">
                  <label htmlFor="question3">Question 3: {vacancy.Question3} *</label>
                  <textarea
                    id="question3"
                    name="question3"
                    rows="4"
                    value={formData.question3}
                    onChange={handleChange}
                    placeholder="Type your answer here..."
                    required={!!vacancy.Question3}
                  ></textarea>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-application-btn" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
              <Link to={getBackLink()} className="cancel-btn">Cancel</Link>
            </div>
          </form>
        </div>
        ) : (
          <div className="application-form-card">
            <h2>Apply for this Position</h2>
            <p className="form-subtitle">
              You need to be logged in as a Student to apply for this position.
            </p>
            <div className="form-actions">
              <Link to="/login" className="submit-application-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
                Login to Apply
              </Link>
              <Link to={getBackLink()} className="cancel-btn">Go Back</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VacancyDetails;

